import { getOperatorLabel, isBlankOperator, isRangeOperator, OPERATORS_BY_FIELD_TYPE, } from '../../utils/field_type_operators.utils';
export const PAGE_SIZE = 20;
export const MAX_BADGE_VALUES_SHOWN = 2;
/**
 * Composite key for a field + operator pair (e.g. `cube.field:equals`).
 */
export const fieldOperatorKey = (fieldKey, operator) => `${fieldKey}:${operator || 'equals'}`;
/**
 * Composite state key that uniquely identifies a filter by its Cube member key
 * AND its operator, so two filters on the same field with different operators
 * (e.g. "equals" and "notEquals") are stored independently.
 */
export const filterStateKey = (f) => fieldOperatorKey(f.key, f.defaultOperator || f.operator);
/** Returns field/operator keys already used by other rows in the edit-filters modal. */
export const getUsedFieldOperatorKeys = (rows, excludeRowId) => {
    const used = new Set();
    for (const row of rows) {
        if (!row.key || row.rowId === excludeRowId) {
            continue;
        }
        used.add(fieldOperatorKey(row.key, row.operator));
    }
    return used;
};
/** First operator for `fieldKey` that is not already used in another row. */
export const getFirstAvailableOperator = (cubeType, fieldKey, usedKeys) => {
    const operators = OPERATORS_BY_FIELD_TYPE[cubeType] || OPERATORS_BY_FIELD_TYPE.string;
    const available = operators.find((o) => !usedKeys.has(fieldOperatorKey(fieldKey, o.value)));
    return available?.value ?? operators[0]?.value ?? 'equals';
};
/** Internal UI sentinel — slot 0 when only the "To" date is set. Never sent to Cube. */
export const DATE_RANGE_FROM_UNSET = '__date_range_from_unset__';
export const isDateRangeFromUnset = (value) => value === DATE_RANGE_FROM_UNSET;
export const dateRangeFromUnsetOption = () => ({
    label: DATE_RANGE_FROM_UNSET,
    value: DATE_RANGE_FROM_UNSET,
});
const dateRangeBoundValues = (opts) => opts.filter((o) => o.value !== NO_VALUE_SENTINEL && !isDateRangeFromUnset(o.value)).map((o) => o.value);
export const hasFilterValues = (operator, opts) => {
    if (isBlankOperator(operator)) {
        return opts.some((o) => o.value === NO_VALUE_SENTINEL);
    }
    const bounds = dateRangeBoundValues(opts);
    if (isDateRangeOperator(operator)) {
        return bounds.length === 2;
    }
    return bounds.length > 0;
};
export const buildFilterParams = (selected, operatorByStateKey, roleByStateKey = {}) => Object.entries(selected)
    .filter(([stateKey, opts]) => hasFilterValues(operatorByStateKey[stateKey] || 'equals', opts))
    .map(([stateKey, opts]) => {
    const colonIdx = stateKey.indexOf(':');
    const member = colonIdx >= 0 ? stateKey.slice(0, colonIdx) : stateKey;
    const operator = operatorByStateKey[stateKey] || 'equals';
    const role = roleByStateKey[stateKey];
    return {
        member,
        operator,
        values: dateRangeBoundValues(opts),
        ...(role ? { role } : {}),
    };
});
/** Build Cube filter params from dashboard filter configs (initial load / default seeding). */
export const buildDefaultFilterParams = (filters, stateKeyForFilter = (filter) => filterStateKey(filter)) => {
    const selected = {};
    const operators = {};
    const roles = {};
    for (const filter of filters) {
        if (!filter.defaultValues?.length) {
            continue;
        }
        const operator = filter.defaultOperator || filter.operator;
        if (!operator) {
            continue;
        }
        const stateKey = stateKeyForFilter(filter);
        selected[stateKey] = filter.defaultValues.map((v) => ({ label: v, value: v }));
        operators[stateKey] = operator;
        roles[stateKey] = filter.role;
    }
    return buildFilterParams(selected, operators, roles);
};
/**
 * Dashboard-filter-specific helpers. Operator definitions and semantics live in
 * the shared field-type operator model so chart conditional formatting reuses
 * the exact same operator set — see SSP-4176 / SSP-3620.
 */
export { FIELD_TYPE_OPERATORS, OPERATORS_BY_FIELD_TYPE, getOperatorConfig, getOperatorLabel, getOperatorsForFieldType, isBlankOperator, isFreeTextOperator, isNumericFieldType, isRangeOperator, isTimeFieldType, normalizeOperator, operatorRequiresValue, toCanonicalFieldType, } from '../../utils/field_type_operators.utils';
/**
 * Short display strings used in filter badges.
 * Keeps badges compact while remaining readable.
 */
export const OPERATOR_BADGE_SHORTHAND = {
    equals: '=',
    notEquals: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    contains: 'contains',
    notContains: 'not contains',
    beforeOrOnDate: 'on or before',
    afterOrOnDate: 'on or after',
    inDateRange: 'in range',
    notInDateRange: 'not in range',
    set: 'not blank',
    notSet: 'blank',
};
const CALENDAR_EXCLUSIVE_CUBE_OPERATORS = {
    beforeDate: { operator: 'lt' },
    afterDate: { operator: 'gte', shiftDays: 1 },
};
/** Value-control label; appends the operator when it is not shown elsewhere in the UI. */
export function getFilterFieldLabel(label, fieldType, operator, translate, options) {
    const includeOperatorInLabel = options?.includeOperatorInLabel ?? true;
    if (!includeOperatorInLabel) {
        return label;
    }
    const opLabelKey = getOperatorLabel(fieldType, operator);
    return opLabelKey ? `${label} (${translate(opLabelKey)})` : label;
}
/**
 * Extract `YYYY-MM-DD` from a Cube-style datetime string and optionally shift
 * it by whole calendar days. Handles both `2026-06-23T14:30:00Z` (ISO 8601)
 * and `2026-06-23 14:30:00` (space-separated, which Cube also returns), and
 * validates month / day ranges so a malformed value like `2026-13-45` is
 * passed through unchanged instead of silently rolling over via `Date.UTC`.
 */
const toCalendarDateValue = (value, shiftDays = 0) => {
    if (typeof value !== 'string') {
        return value;
    }
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return value;
    }
    const [dateOnly, yearStr, monthStr, dayStr] = match;
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return value;
    }
    if (!shiftDays) {
        return dateOnly;
    }
    const shifted = new Date(Date.UTC(year, month - 1, day + shiftDays));
    const pad = (n) => String(n).padStart(2, '0');
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
};
export const normalizeCubeFilterParam = (filter) => {
    const mapping = CALENDAR_EXCLUSIVE_CUBE_OPERATORS[filter.operator];
    if (!mapping) {
        return filter;
    }
    return {
        ...filter,
        operator: mapping.operator,
        values: (filter.values ?? []).map((v) => toCalendarDateValue(v, mapping.shiftDays)),
    };
};
/** Options for one filter row, excluding fields already selected in other rows. */
export function getFilterFieldOptionsForRow(allFields, selectedKeys, currentRowKey) {
    const usedByOthers = new Set(selectedKeys.filter((key) => key && key !== currentRowKey));
    return allFields.filter((field) => !usedByOthers.has(field.key));
}
/** True when at least one dataset dimension is not yet assigned to a filter row. */
export function hasAvailableFilterFields(allFields, selectedKeys) {
    const usedKeys = new Set(selectedKeys.filter(Boolean));
    return usedKeys.size < allFields.length;
}
/** Alias retained for FilterBar dashboard filter code. */
export const isNoValueOperator = isBlankOperator;
/** Alias retained for FilterBar dashboard filter code. */
export const isDateRangeOperator = isRangeOperator;
/**
 * Sentinel stored in SelectOption state to mark a no-value filter as active.
 * Consumers should emit `values: []` to Cube when they see this sentinel.
 */
export const NO_VALUE_SENTINEL = '__no_value__';
